import React, { useEffect } from "react";
import { CategoryTabs } from "../welcome/CategoryTabs";
import { ActionCard } from "../welcome/ActionCard";
import { useQuickActionsStore } from "@store";
import { CategoryType } from "../welcome/types";
import { trackEvent } from "../../services/analyticsService";
import { logDebug } from "../../utils/logging";
import gridStyles from "../welcome/QuickActionGrid.module.css";
import pageStyles from "./PromptsPage.module.css";
import { useGuidedFlowStore, useUIStore } from "@store";

const CATEGORIES: CategoryType[] = [
  "Toate",
  "Copywriting",
  "Document",
  "Sfaturi Strategice",
  "Altele",
];

const PromptsPage: React.FC = () => {
  const navigate = useUIStore((s) => s.navigate);
  const {
    templates,
    filteredTemplates,
    selectedCategory,
    isLoading,
    error,
    loadTemplates,
    setSelectedCategory,
    setSelectedCardType,
  } = useQuickActionsStore();

  // Load ALL templates once; no welcome cards
  useEffect(() => {
    logDebug("[PromptsPage] Loading all templates (no card_type filter)");
    // Explicitly clear any card type filter to show ALL templates
    setSelectedCardType(null);

    // Just load templates. We pass null explicitly (though store handles undefined too)
    loadTemplates(null, selectedCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCategoryChange = (category: CategoryType) => {
    setSelectedCategory(category);
    // Re-filter using store logic (already updates filteredTemplates)
    trackEvent("prompts_category_changed", { category });
  };

  const handleTemplateSelect = (
    template: ReturnType<
      typeof useQuickActionsStore.getState
    >["templates"][number],
  ) => {
    logDebug("[PromptsPage] Template selected", template.template_id);
    // Start guided flow and route to chat view
    useGuidedFlowStore.getState().startFlow(template);
    navigate("chat");
    trackEvent("prompts_template_selected", {
      template_id: template.template_id,
      category: template.category,
    });
  };

  return (
    <div className={pageStyles.page}>
      <h1 className={pageStyles.title}>Prompturi</h1>

      {error && (
        <div className={`${gridStyles.errorState} ${pageStyles.errorSpacing}`}>
          <p className={gridStyles.errorMessage}>
            Nu am putut încărca șabloanele. Reîncearcă.
          </p>
          <button
            className={gridStyles.retryButton}
            onClick={() => loadTemplates(null, selectedCategory)}
          >
            Reîncearcă
          </button>
        </div>
      )}

      {/* Category filter */}
      <CategoryTabs
        categories={CATEGORIES}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
      />

      <div
        className={gridStyles.templateGrid}
        role="list"
        aria-label="Lista șabloanelor disponibile"
      >
        {isLoading ? (
          <div className={gridStyles.loadingState}>
            <p>Se încarcă șabloanele...</p>
          </div>
        ) : (
          (filteredTemplates.length > 0 ? filteredTemplates : templates).map(
            (template) => (
              <ActionCard
                key={template.template_id}
                template={template}
                onClick={() => handleTemplateSelect(template)}
              />
            ),
          )
        )}
      </div>
    </div>
  );
};

export default PromptsPage;
